import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {SafeHtml} from '@angular/platform-browser';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';
import {getSecurityNameExplained, getSecurityNameOrExchange} from '../../../common/display-symbol';
import {squash} from '../../../common/rxjs/squash';
import {destroy$} from '../../../components/destroyable';
import {WIDGET_UPDATES_GATING$} from '../../../components/layout/widget/widget-updates-gating';
import {SecurityService} from '../../../components/security';
import {isAsecurityKey} from '../../../components/security/aspreader-security';
import {DisplayDecimal} from '../../../components/symbol-decimals';
import {Security} from '../../../models/security';
import {formatSecurityKey, SecurityKey} from '../../../models/security-key';
import {HtmlSymbolService} from '../../../services/html-symbol.service';
import {DisplayQuote, DisplayQuoteFactory} from '../../quotes-widget/display-quote.factory';
import {QuotesStreamingService} from '../quotes-streaming';
import {Quote} from '../quotes-streaming/quote';
import {QuoteDetails} from './quote-details';

@Component({
    selector: 'quote-details',
    templateUrl: './quote-details.component.html',
    styleUrls: ['./quote-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuoteDetailsComponent{

    @Input()
    public set securityKey(securityKey: SecurityKey) {
        this.securityKey$.next(securityKey);
    }
    public get securityKey(): SecurityKey {
        return this.securityKey$.value;
    }

    @Output()
    public tradePrice: EventEmitter<any> = new EventEmitter<any>();

    public readonly destroy$ = destroy$();

    public displayQuote: DisplayQuote;

    public isPercentSymbolNeeded: boolean = false;

    public quoteDetails: QuoteDetails;

    public readonly mdash = this.htmlSymbolService.mdash();

    public doHideLastPriceInfo: boolean;

    protected securityKeyStr: string;

    protected securityKey$ = new BehaviorSubject<SecurityKey>(null);

    private readonly emptyDisplayDecimal: DisplayDecimal<SafeHtml> = {
        value: this.mdash,
        class: ''
    };

    constructor(
        protected htmlSymbolService: HtmlSymbolService,
        protected quotesStreamingService: QuotesStreamingService,
        protected securityService: SecurityService,
        protected displayQuoteFactory: DisplayQuoteFactory
    ) {
        this.quoteDetails = this.getEmptyQuoteDetails();
        this.initSubscriptions();
    }

    protected initSubscriptions(): void {
        this.securityKey$.pipe(
            filter(securityKey => !!securityKey),
            tap(securityKey => this.securityKeyStr = formatSecurityKey(securityKey)),
            tap(() => this.quoteDetails = this.getEmptyQuoteDetails()),
            tap(securityKey => this.doHideLastPriceInfo = isAsecurityKey(securityKey)),
            switchMap(() => this.listenToQuotes()),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    protected listenToQuotes(): Observable<any> {
        return this.quotesStreamingService.createSubscription([this.securityKeyStr]).pipe(
            takeUntil(this.destroy$),
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$
            }),
            tap((quote: Quote) => this.updateQuoteDetails(quote))
        );
    }

    protected getEmptyQuoteDetails(): QuoteDetails {
        const knownSecurity: Security = this.securityKey
            && this.securityService.getSecurityFromCacheBySecurityKey(this.securityKey);

        return {
            symbol:                knownSecurity && knownSecurity.symbol || this.mdash,
            securityName:          knownSecurity && getSecurityNameOrExchange(knownSecurity) || this.mdash,
            securityNameExplained: getSecurityNameExplained(knownSecurity),
            tradePrice:            this.emptyDisplayDecimal,
            changeValue:           this.emptyDisplayDecimal,
            changePercent:         this.emptyDisplayDecimal,
            bidPrice:              this.emptyDisplayDecimal,
            bidVolume:             this.emptyDisplayDecimal,
            askPrice:              this.emptyDisplayDecimal,
            askVolume:             this.emptyDisplayDecimal
        };
    }

    // tslint:disable:cyclomatic-complexity
    private updateQuoteDetails(quote: Quote): void {
        let displayQuote: DisplayQuote = this.displayQuote;

        if (displayQuote) {
            this.displayQuoteFactory.updateDisplayQuote(displayQuote, quote);
        } else {
            displayQuote = this.displayQuoteFactory.createDisplayQuote(quote);
        }

        this.displayQuote = displayQuote;

        const tradePrice = displayQuote.tradePrice;

        this.quoteDetails = {
            symbol:                quote.security.symbol || this.mdash,
            securityName:          getSecurityNameOrExchange(quote.security) || this.mdash,
            securityNameExplained: getSecurityNameExplained(quote.security),
            bidPrice:              this.dashIfEmpty(displayQuote.bid.price.displayValue),
            bidVolume:             this.dashIfEmpty(displayQuote.bid.volume.displayValue),
            askPrice:              this.dashIfEmpty(displayQuote.ask.price.displayValue),
            askVolume:             this.dashIfEmpty(displayQuote.ask.volume.displayValue),
            tradePrice:            this.dashIfEmpty(tradePrice.displayValue),
            changeValue:           this.dashIfEmpty(tradePrice.displayChangeValue),
            changePercent:         this.dashIfEmpty(tradePrice.displayChangePercent)
        };

        this.isPercentSymbolNeeded = !!tradePrice.displayChangePercent;

        this.tradePrice.emit(this.displayQuote.tradePrice.value);
    }

    private dashIfEmpty(displayDecimal: DisplayDecimal): DisplayDecimal<SafeHtml> {
        return displayDecimal && displayDecimal.value
            ? displayDecimal as DisplayDecimal<SafeHtml>
            : this.emptyDisplayDecimal;
    }

}
